import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// ######### MATERIAL UI
import { makeStyles, Drawer, Box, Typography, Chip } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import Refresh from "@material-ui/icons/Refresh";
import { Cancel } from "@material-ui/icons";
import { ThreeDots } from "react-loader-spinner";

// ######### CUSTOM COMPONENTS
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CleanButton from "components/CustomButtons/CleanButton.js";
import Button from "components/CustomButtons/Button.js";

// ######### INTERNAL COMPONENTS
import NavigationTabs from "./NavigationTabs";
import TabConfiguration from "./TabConfiguration";
import TabTablePreview from "./TabTablePreview";
import TabModelingRoute from "./TabModelingRoute";
import TabVariantManagement from "./TabVariantManagement";

// ######### API CALLS
import { getVariants, getTableData } from "../../viewServices/apiCalls";

// ######### STYLES
const drawerStyles = (theme) => ({
  drawerPaper: {
    width: "70%",
    margin: "10px",
    borderRadius: "5px",
    maxHeight: "calc(100% - 20px)",
    display: "flex",
    flexDirection: "column",
    boxShadow: "none",
  },
  drawerHeader: {
    flexShrink: 0,
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
  },
  drawerContent: {
    flex: 1,
    overflow: "auto",
    padding: "16px",
  },
  drawerFooter: {
    flexShrink: 0,
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    padding: "16px",
  },
  drawerCard: {
    marginTop: 0,
    marginBottom: 0,
  },
  cardHeaderTitle: {
    ...theme.typography.h6,
    fontSize: "1.125rem",
    color: theme.palette.secondary.main,
    fontWeight: "400",
    marginBottom: "10px",
    marginTop: "10px",
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  cardButton: {
    "&.MuiButtonBase-root": {
      boxShadow: "none",
    },
    "&.MuiIconButton-root": {
      boxShadow: "none",
    },
  },
  editButton: {
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    color: "#666666",
    padding: "8px 16px",
    borderRadius: "8px",
    textTransform: "none",
    fontSize: "0.875rem",
    fontWeight: "500",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: "#d0d0d0",
      color: "#666666",
    },
    "& .MuiButton-startIcon": {
      marginRight: "8px",
    },
  },
  cancelButton: {
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    color: "#666666",
    padding: "8px 16px",
    borderRadius: "8px",
    textTransform: "none",
    fontSize: "0.875rem",
    fontWeight: "500",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: "#d0d0d0",
      color: "#666666",
    },
    "& .MuiButton-startIcon": {
      marginRight: "8px",
    },
  },
  variantChip: {
    cursor: "pointer",
    minWidth: "80px",
    height: "27px",
    borderRadius: "16px",
    border: "1px solid #e0e0e0",
    backgroundColor: "#ffffff",
    color: "#666666",
    margin: theme.spacing(0.5),
    "& .MuiChip-label": {
      whiteSpace: "normal",
      textAlign: "center",
      lineHeight: "1.2",
      paddingLeft: "12px",
      paddingRight: "12px",
    },
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: "#d0d0d0",
    },
    "&.selected": {
      backgroundColor: (props) =>
        props.userDetails?.implementation_color || "#1976d2",
      borderColor: (props) =>
        props.userDetails?.implementation_color || "#1976d2",
      color: "#ffffff",
      "&:hover": {
        backgroundColor: (props) =>
          props.userDetails?.implementation_color || "#1976d2",
        borderColor: "#d0d0d0",
      },
    },
  },
  variantContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    alignItems: "center",
  },
});

const useStyles = makeStyles(drawerStyles);

// ######### COMPONENT
export default function DrawerFileIngestion({
  open,
  onClose,
  selectedInputTable,
  inputMetadata,
  inputColumns,
  inputFiles,
  ingestionColumns,
  isLoadingMetadata,
  isLoadingColumns,
  onMetadataRefresh,
  onColumnsRefresh,
  userDetails,
}) {
  const classes = useStyles({ userDetails });
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });

  // ######### INTERNAL STATES
  const [activeViewTab, setActiveViewTab] = useState(
    selectedInputTable === "model01" ? "modeling_route_tab" : "table_tab",
  );
  const [readonly, setReadonly] = useState(true);
  const [isFilePreparedInTabView, setIsFilePreparedInTabView] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [isLoadingTableData, setIsLoadingTableData] = useState(false);
  const [tableError, setTableError] = useState(null);
  const submissionRef = useRef(null);

  // ######### METHODS
  const handleTabChange = (newTab) => {
    // Prevent switching to ANY tab (except configuration_tab) when in edit mode
    if (newTab !== "configuration_tab" && !readonly) {
      return;
    }

    if (
      activeViewTab === "configuration_tab" &&
      newTab !== "configuration_tab" &&
      !readonly
    ) {
      setReadonly(true);
      // Don't cleanup temp blob on tab switch - keep it available for retry
      if (isFilePreparedInTabView) {
        setIsFilePreparedInTabView(false);
        console.log("Tab switch - keeping prepared file for potential retry");
      }
    }
    setActiveViewTab(newTab);
  };

  const fetchVariants = async () => {
    try {
      const response = await getVariants();
      setVariants(response);
    } catch (err) {
      console.error("Failed to fetch variants", err);
    }
  };

  const fetchTableData = async (tableId, variantId) => {
    if (!tableId || !variantId) return;

    setIsLoadingTableData(true);
    setTableError(null);

    try {
      const response = await getTableData(tableId, variantId, 100);

      if (response && response.data) {
        const rawHeaders = Object.keys(response.data[0] || {});

        // Map column IDs to readable column names
        const readableHeaders = rawHeaders.map((columnId) => {
          const columnMetadata = inputColumns[tableId]?.columns?.[columnId];
          return columnMetadata?.column_name || columnId;
        });

        const rows = response.data.map((row) =>
          rawHeaders.map((header) => row[header]),
        );

        // Store data in an object keyed by tableId
        setTableData((prevData) => ({
          ...prevData,
          [tableId]: {
            headers: readableHeaders,
            rows,
          },
        }));
      }
    } catch (err) {
      setTableError(err.response?.data?.error || "Error fetching table data");
      setTableData((prevData) => ({
        ...prevData,
        [tableId]: null,
      }));
    } finally {
      setIsLoadingTableData(false);
    }
  };

  const handleCloseDrawer = () => {
    // Check if upload is in progress - just log it, don't prevent closure
    if (submissionRef.current?.isUploadInProgress?.()) {
      console.log(
        "Drawer closed during active upload - polling will continue in background",
      );
    }

    // Reset states when closing (except upload tracking which continues)
    setReadonly(true);
    setIsFilePreparedInTabView(false);
    setActiveViewTab(
      selectedInputTable === "model01" ? "modeling_route_tab" : "table_tab",
    );
    onClose();
  };

  // ######### EFFECTS

  // Reset states when drawer opens/closes or table changes
  useEffect(() => {
    if (open && selectedInputTable) {
      setActiveViewTab(
        selectedInputTable === "model01" ? "modeling_route_tab" : "table_tab",
      );
      setReadonly(true);
      setIsFilePreparedInTabView(false);
      setSelectedVariant(null);
    }
  }, [open, selectedInputTable]);

  // Fetch variants when component mounts or when needed
  useEffect(() => {
    if (open) {
      fetchVariants();
    }
  }, [open]);

  // Set first variant as default when variants are loaded
  useEffect(() => {
    if (selectedInputTable && variants[selectedInputTable]) {
      const firstVariant = variants[selectedInputTable][0];
      if (firstVariant) {
        setSelectedVariant(firstVariant.variant_id);
      }
    }
  }, [selectedInputTable, variants]);

  // Fetch table data when variant changes
  useEffect(() => {
    if (selectedInputTable && selectedVariant) {
      fetchTableData(selectedInputTable, selectedVariant);
    }
  }, [selectedInputTable, selectedVariant]);

  // Cleanup effect
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log(
        "Page unload - temp blob will be naturally overwritten by next file preparation",
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      console.log(
        "Component unmount - temp blob will be naturally overwritten by next file preparation",
      );
    };
  }, [readonly, isFilePreparedInTabView, selectedInputTable]);

  // ######### RENDER
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCloseDrawer}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      {/* ===== HEADER - FIXED ===== */}
      <div className={classes.drawerHeader}>
        {/* Navigation Tabs */}
        <Card className={classes.drawerCard}>
          <CardHeader style={{ padding: "0px 10px" }}>
            <GridContainer>
              <GridItem xs={12} sm={12}>
                <NavigationTabs
                  value={activeViewTab}
                  onChange={handleTabChange}
                  color={userDetails.implementation_color}
                  options={(() => {
                    const baseTabs = [];

                    // Tab "Modeling Route" as FIRST option if model01
                    if (selectedInputTable === "model01") {
                      baseTabs.push({
                        value: "modeling_route_tab",
                        label: t("modeling_route_tab"),
                        disabled: !readonly,
                        tooltip: !readonly
                          ? t("table_preview.not_available_in_edit_mode")
                          : undefined,
                      });
                    }

                    // Tab Table Preview (disabled in edit mode or no variant)
                    baseTabs.push({
                      value: "table_tab",
                      label: t("table_tab"),
                      disabled: !selectedVariant || !readonly,
                      tooltip: !selectedVariant
                        ? t("select_variant_first")
                        : !readonly
                          ? t("table_preview.not_available_in_edit_mode")
                          : undefined,
                    });

                    // Tab Configuration (always enabled)
                    baseTabs.push({
                      value: "configuration_tab",
                      label: t("configuration_tab"),
                      disabled: false,
                    });

                    // Tab Variant Management (disabled in edit mode)
                    baseTabs.push({
                      value: "variant_management_tab",
                      label: t("variant_management"),
                      disabled: !readonly,
                      tooltip: !readonly
                        ? t("table_preview.not_available_in_edit_mode")
                        : undefined,
                    });

                    return baseTabs;
                  })()}
                />
              </GridItem>
            </GridContainer>
          </CardHeader>
        </Card>

        {/* Modeling Route Tab Header */}
        {activeViewTab === "modeling_route_tab" &&
          selectedInputTable === "model01" && (
            <Card className={classes.drawerCard}>
              <CardBody style={{ padding: "16px" }}>
                <Box>
                  <h4
                    className={classes.cardHeaderTitle}
                    style={{ marginBottom: "4px" }}
                  >
                    {selectedInputTable &&
                      inputMetadata[selectedInputTable]?.table_description}
                  </h4>
                  <Typography
                    variant="body2"
                    style={{
                      fontSize: "0.75rem",
                      color: "#999",
                      marginBottom: "12px",
                    }}
                  >
                    {t(
                      variants[selectedInputTable]?.length === 1
                        ? "model_variants_count"
                        : "model_variants_count_plural",
                      { count: variants[selectedInputTable]?.length || 0 },
                    )}{" "}
                    {t("variants_in_total")}
                  </Typography>
                  <Box className={classes.variantContainer}>
                    {variants[selectedInputTable] &&
                    variants[selectedInputTable].length > 0 ? (
                      variants[selectedInputTable].map((variant) => (
                        <Chip
                          key={variant.variant_id}
                          label={variant.variant_name}
                          onClick={() => setSelectedVariant(variant.variant_id)}
                          className={
                            selectedVariant === variant.variant_id
                              ? `${classes.variantChip} selected`
                              : classes.variantChip
                          }
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {t("no_variants_available")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardBody>
            </Card>
          )}

        {/* Configuration Tab Header */}
        {activeViewTab === "configuration_tab" && (
          <Card className={classes.drawerCard}>
            <CardBody style={{ padding: "8px 16px" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="baseline"
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <h4 className={classes.cardHeaderTitle}>
                    {selectedInputTable &&
                      inputMetadata[selectedInputTable]?.table_description}
                  </h4>
                  {isUploadInProgress && (
                    <Chip
                      size="small"
                      label={t("upload_in_progress")}
                      style={{
                        backgroundColor: userDetails.implementation_color,
                        color: "#fff",
                        fontWeight: 500,
                        animation: "pulse 2s ease-in-out infinite",
                      }}
                      icon={
                        <div
                          style={{
                            width: "12px",
                            height: "12px",
                            border: "2px solid #fff",
                            borderTopColor: "transparent",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                            marginLeft: "8px",
                          }}
                        />
                      }
                    />
                  )}
                  <style>{`
                    @keyframes pulse {
                      0%, 100% { opacity: 1; }
                      50% { opacity: 0.7; }
                    }
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </Box>
                <div>
                  {readonly ? (
                    <Tooltip placement="top" title={t("edit_button")}>
                      <CleanButton
                        startIcon={<EditIcon />}
                        onClick={() => setReadonly(false)}
                        className={classes.editButton}
                        disabled={isUploadInProgress}
                      >
                        {t("edit_button")}
                      </CleanButton>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      placement="top"
                      title={
                        isUploadInProgress
                          ? t("cannot_cancel_during_upload")
                          : t("cancel_button")
                      }
                    >
                      <span>
                        <CleanButton
                          startIcon={<Cancel />}
                          onClick={() => {
                            console.log(
                              "Cancel clicked - keeping prepared file for potential retry",
                            );
                            setReadonly(true);
                            setIsFilePreparedInTabView(false);
                          }}
                          className={classes.cancelButton}
                          disabled={isUploadInProgress}
                        >
                          {t("cancel_button")}
                        </CleanButton>
                      </span>
                    </Tooltip>
                  )}
                </div>
              </Box>
            </CardBody>
          </Card>
        )}

        {/* Table Tab Header */}
        {activeViewTab === "table_tab" && (
          <Card className={classes.drawerCard}>
            <CardBody style={{ padding: "8px 16px" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <h4 className={classes.cardHeaderTitle}>
                  {selectedInputTable &&
                    inputMetadata[selectedInputTable]?.table_description}
                </h4>
                <Box display="flex" alignItems="center">
                  <Box className={classes.variantContainer}>
                    {variants[selectedInputTable] &&
                    variants[selectedInputTable].length > 0 ? (
                      variants[selectedInputTable].map((variant) => (
                        <Chip
                          key={variant.variant_id}
                          label={variant.variant_name}
                          onClick={() => setSelectedVariant(variant.variant_id)}
                          className={
                            selectedVariant === variant.variant_id
                              ? `${classes.variantChip} selected`
                              : classes.variantChip
                          }
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {t("no_variants_available")}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip placement="top" title={t("refresh_data")}>
                    <Button
                      justIcon
                      size="sm"
                      color="client"
                      onClick={() =>
                        fetchTableData(selectedInputTable, selectedVariant)
                      }
                      disabled={isLoadingTableData}
                      className={classes.cardButton}
                      style={{ marginLeft: "24px" }}
                    >
                      <Refresh />
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </CardBody>
          </Card>
        )}
      </div>

      {/* ===== CONTENT - SCROLLABLE ===== */}
      <div className={classes.drawerContent}>
        {isLoadingColumns || isLoadingMetadata ? (
          <div className={classes.loadingBox}>
            <ThreeDots
              color={userDetails.implementation_color}
              ariaLabel="loading"
            />
          </div>
        ) : (
          <>
            {activeViewTab === "modeling_route_tab" &&
              selectedInputTable === "model01" && (
                <TabModelingRoute
                  selectedInputTable={selectedInputTable}
                  inputMetadata={inputMetadata}
                  inputColumns={inputColumns}
                  userDetails={userDetails}
                  selectedVariant={selectedVariant}
                />
              )}

            {activeViewTab === "configuration_tab" && (
              <TabConfiguration
                ref={submissionRef}
                selectedInputTable={selectedInputTable}
                inputColumns={inputColumns}
                inputFiles={inputFiles}
                ingestionColumns={ingestionColumns}
                loadColumnsAndFiles={onColumnsRefresh}
                onMetadataChange={onMetadataRefresh}
                onVariantsRefresh={fetchVariants}
                variants={variants[selectedInputTable] || []}
                selectedVariant={selectedVariant}
                setSelectedVariant={setSelectedVariant}
                readonly={readonly}
                onReadonlyChange={setReadonly}
                onFilePreparedChange={setIsFilePreparedInTabView}
                onSubmissionStatusChange={setIsSubmitDisabled}
                onUploadStatusChange={setIsUploadInProgress}
                hideFooter={true}
              />
            )}

            {activeViewTab === "table_tab" && (
              <div
                style={{
                  position: "relative",
                  flex: "1",
                  minWidth: "400px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TabTablePreview
                  tableId={selectedInputTable}
                  tableData={tableData?.[selectedInputTable]}
                  isLoading={isLoadingTableData}
                  error={tableError}
                  onRefresh={() =>
                    fetchTableData(selectedInputTable, selectedVariant)
                  }
                  variants={variants[selectedInputTable] || []}
                  selectedVariant={selectedVariant}
                  setSelectedVariant={setSelectedVariant}
                />
              </div>
            )}

            {activeViewTab === "variant_management_tab" && (
              <TabVariantManagement
                selectedInputTable={selectedInputTable}
                variants={variants[selectedInputTable] || []}
                onVariantsChange={fetchVariants}
              />
            )}
          </>
        )}
      </div>

      {/* ===== FOOTER - FIXED (only for configuration tab) ===== */}
      {activeViewTab === "configuration_tab" && (
        <div className={classes.drawerFooter}>
          {!readonly && (
            <Box display="flex" justifyContent="center" mb={2}>
              <Button
                variant="contained"
                style={{
                  backgroundColor: userDetails.implementation_color,
                  color: "#fff",
                }}
                startIcon={<SaveIcon />}
                disabled={isSubmitDisabled}
                onClick={() => submissionRef.current.submit()}
              >
                {t("save_and_process")}
              </Button>
            </Box>
          )}
          <Box
            style={{
              backgroundColor: "#f5f5f5",
              padding: "12px",
              borderRadius: "4px",
              fontSize: "0.875rem",
              color: "#666",
            }}
          >
            <Typography>{t("no_warning")}</Typography>
          </Box>
        </div>
      )}
    </Drawer>
  );
}

// ######### PROP TYPES
DrawerFileIngestion.propTypes = {
  // Controle de abertura
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,

  // Tabela selecionada
  selectedInputTable: PropTypes.string.isRequired,

  // Dados globais (read-only)
  inputMetadata: PropTypes.object.isRequired,
  inputColumns: PropTypes.object.isRequired,
  inputFiles: PropTypes.object.isRequired,
  ingestionColumns: PropTypes.object.isRequired,

  // Loading states globais
  isLoadingMetadata: PropTypes.bool.isRequired,
  isLoadingColumns: PropTypes.bool.isRequired,

  // Callbacks para atualizar dados globais no pai
  onMetadataRefresh: PropTypes.func.isRequired,
  onColumnsRefresh: PropTypes.func.isRequired,

  // User details
  userDetails: PropTypes.object.isRequired,
};
